import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addFlow46LCA,
  deleteFlow46LCA,
  getAllFlow46LCA,
  updateFlow46LCA,
} from "../../../../../apis/Product/BeyondProductLifeCycleFlow46Api";
import {
  getProductFlow46MaterialDropdown,
  getProductFlow46NamePartInFUDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const Flow46Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [namePartInFUDropdown, setNamePartInFUDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [unitMaterial, setUnitMaterial] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductFlow46MaterialDropdown({
      setDropdown: setMaterialDropdown,
    });
    getProductFlow46NamePartInFUDropdown({
      productId,
      setDropdown: setNamePartInFUDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, [productId]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllFlow46LCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Convert country codes to names when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          const updates = {};
          
          // Convert country_of_origin code to name
          if (item.country_of_origin && typeof item.country_of_origin === "string") {
            if (item.country_of_origin.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.country_of_origin
              );
              if (foundCountry && item.country_of_origin !== foundCountry.name) {
                hasChanges = true;
                updates.country_of_origin = foundCountry.name;
              }
            }
          }
          
          return hasChanges ? { ...item, ...updates } : item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && materialDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.what_material_prevent_purchase === "string"
            ? row.what_material_prevent_purchase
            : row.what_material_prevent_purchase?.value || row.what_material_prevent_purchase?.code;
        if (materialCode) {
          const selected = materialDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === materialCode ||
                opt.value === materialCode ||
                opt.name === materialCode ||
                String(opt.value) === String(materialCode))
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitMaterial(newUnits);
    }
  }, [data, materialDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      what_material_prevent_purchase: {
        targetField: "quantity_per_fu",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          _rowData,
          rowId = "new",
          fullOption
        ) => {
          if (
            fullOption &&
            typeof fullOption === "object" &&
            fullOption.unit != null &&
            fullOption.unit !== ""
          ) {
            setUnitMaterial((prev) => ({
              ...prev,
              [rowId]: fullOption.unit,
            }));
            return;
          }
          const selected = dropdownData?.find(
            (opt) =>
              !opt.isCategory &&
              (opt?.value === selectedValue ||
                opt?.code === selectedValue ||
                opt?.name === selectedValue ||
                String(opt?.value) === String(selectedValue))
          );
          if (selected) {
            setUnitMaterial((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "what_material_prevent_purchase",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialCode =
        typeof editedItem.what_material_prevent_purchase === "string"
          ? editedItem.what_material_prevent_purchase
          : editedItem.what_material_prevent_purchase?.value || 
            editedItem.what_material_prevent_purchase?.code || 
            null;

      // Extract country code from object or convert name to code
      let countryCode = editedItem.country_of_origin;
      if (countryCode) {
        if (typeof countryCode === "object") {
          // If it's an object, extract the code
          countryCode = countryCode.code || countryCode.value || null;
        } else if (typeof countryCode === "string") {
          // If it's a string, check if it's already a code (2-3 chars) or a name
          if (countryCode.length <= 3) {
            // Already a code, use it
            countryCode = countryCode;
          } else {
            // It's a name, find the code
            const foundCountry = countryDropdown.find(
              (option) => option.name === countryCode
            );
            if (foundCountry) {
              countryCode = foundCountry.code;
            }
          }
        }
      }

      updateFlow46LCA({
        productId,
        rowId: editedItem.id,
        name_part_in_fu: editedItem.name_part_in_fu || null,
        what_material_prevent_purchase: materialCode,
        quantity_per_fu: editedItem.quantity_per_fu || 0,
        country_of_origin: countryCode,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-beyond-product-life-cycle-46-totals'));
        },
      });
    },
    [productId, data, countryDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialCode =
        typeof newItem.what_material_prevent_purchase === "string"
          ? newItem.what_material_prevent_purchase
          : newItem.what_material_prevent_purchase?.value || 
            newItem.what_material_prevent_purchase?.code || 
            null;

      // Extract country code from object or convert name to code
      let countryCode = newItem.country_of_origin;
      if (countryCode) {
        if (typeof countryCode === "object") {
          // If it's an object, extract the code
          countryCode = countryCode.code || countryCode.value || null;
        } else if (typeof countryCode === "string") {
          // If it's a string, check if it's already a code (2-3 chars) or a name
          if (countryCode.length <= 3) {
            // Already a code, use it
            countryCode = countryCode;
          } else {
            // It's a name, find the code
            const foundCountry = countryDropdown.find(
              (option) => option.name === countryCode
            );
            if (foundCountry) {
              countryCode = foundCountry.code;
            }
          }
        }
      }

      addFlow46LCA({
        productId,
        name_part_in_fu: newItem.name_part_in_fu || null,
        what_material_prevent_purchase: materialCode,
        quantity_per_fu: newItem.quantity_per_fu || 0,
        country_of_origin: countryCode,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-beyond-product-life-cycle-46-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFlow46LCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-beyond-product-life-cycle-46-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "name_part_in_fu",
        accessor: "name_part_in_fu",
        header: t("NAME_PART_IN_FU") || "Name the part in the FU",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        hasDropdown: true,
        dropdownDataKey: "name_part_in_fu",
      },
      {
        id: "what_material_prevent_purchase",
        accessor: "what_material_prevent_purchase",
        header: `${t("WHAT_MATERIAL_PREVENT_PURCHASE") || "What material do we prevent the purchase of"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_MATERIAL") || "Select material...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "what_material_prevent_purchase",
            materialDropdown,
            unitMaterial,
            rowId
          ),
      },
      {
        id: "quantity_per_fu",
        accessor: "quantity_per_fu",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "what_material_prevent_purchase",
            materialDropdown,
            unitMaterial,
            rowId
          ),
      },
      {
        id: "country_of_origin",
        accessor: "country_of_origin",
        header: `${t("COUNTRY_OF_ORIGIN_PREVENTED_PURCHASE") || "Country of origin of prevented purchase"} *`,
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitMaterial,
      materialDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      name_part_in_fu: namePartInFUDropdown || [],
      what_material_prevent_purchase: materialDropdown || [],
      country_of_origin: countryDropdown || [],
    }),
    [namePartInFUDropdown, materialDropdown, countryDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default Flow46Table;

